import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { AppService } from "src/app/app.service";
import { CompDetails } from "./comps-page.model";
import { catchError } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class CompsDataService {
  apiEP: string = window.apiUrl;
  legacyUrl: string = window.caUrl;

  constructor(
    private http: HttpClient,
    private appService: AppService,
  ) {}

  getCompsMeta(params: string): Observable<any> {
    return this.http.get(`${this.apiEP}/comps/get_comps_meta?${params}`);
  }

  newCompsMeta(compDetails: CompDetails) {
    return this.http.post(`${this.apiEP}/comps/new_comps_meta`, compDetails);
  }

  getCompsData(params: string): Observable<any> {
    return this.http.get(
      `${this.apiEP}/comps/get_universal_comps_data?${params}`,
    );
  }

  updateLineItems(currency, tabId, data, marketDataItems): Observable<any> {
    const dataToSend = {
      col_info: data,
      currency,
      tab_id: tabId,
      market_data: marketDataItems,
    };
    return this.http.post(
      `${this.apiEP}/comps/add_update_comps_col_meta`,
      dataToSend,
    );
  }

  updateTickers(data): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/add_update_comps_row_meta`,
      data,
    );
  }

  getStdLineItems(reqObj): Observable<any> {
    reqObj["show_val_ratio"] = true;
    return this.http.post(`${this.apiEP}/comps/get_std_line_items`, reqObj);
  }

  getSuggestedLineItems(tickers: Array<any>): Observable<any> {
    return this.http.get(
      `${
        this.apiEP
      }/charts_page/get_lineitem_suggestions/?tickers=${tickers.join(
        "&tickers=",
      )}&cut=STD&is_opssli=true`,
    );
  }

  getWatchlists(): Observable<any> {
    return this.http.get(`${this.apiEP}/comps/get_user_watchlist_for_comps/`);
  }

  getRecentlyVisitedCompanies(): Observable<any> {
    return this.http.get(
      `${this.apiEP}/comps/get_recently_visited_active_companies`,
    );
  }

  deleteTicker(deleteTickerObj): Observable<any> {
    return this.http.post(
      `${this.apiEP}/comps/delete_col_row_meta`,
      deleteTickerObj,
    );
  }
  getAvailableCurrencies(): Observable<any> {
    return this.http.get(`${this.apiEP}/api/get_available_currencies`);
  }

  saveCurrency(tabId, selectedCurrency): Observable<any> {
    const reqObj = {
      tab_id: tabId,
    };
    if (selectedCurrency !== "Base") {
      reqObj["user_currency"] = selectedCurrency;
    }
    return this.http.post(`${this.apiEP}/comps/save_currency`, reqObj);
  }

  updateCompsMeta(reqObj): Observable<any> {
    return this.http.post(`${this.apiEP}/comps/update_comps_meta`, reqObj);
  }

  editDeleteTabDetails(tabId, tabName) {
    let params = `tab_id=${tabId}`;
    if (tabName !== "") {
      params += `&tab_name=${encodeURIComponent(tabName)}`;
    }
    return this.http.get(
      `${this.apiEP}/comps/edit_delete_tab_details?${params}`,
    );
  }

  saveVaComps(currentTabId) {
    const reqObj = {
      is_active: true,
      tab_id: currentTabId,
    };
    return this.http.post(`${this.apiEP}/comps/save_vacomps_copy`, reqObj);
  }

  updateLeftPanel(getData) {
    if (getData) {
      this.appService.updateLeftPanelSource.next({ dataRefresh: "true" });
    } else {
      this.appService.updateLeftPanelSource.next({ dataRefresh: "false" });
    }
  }

  exportComps(params, isNewAPI = false) {
    if (isNewAPI)
      return this.http.get(
        `${this.apiEP}/addin/export?${params}&template_type=comps`,
      );
    return this.http.get(`${this.apiEP}/comps_export/export?${params}`);
  }

  exportComparativeComps(params, isNewAPI = false) {
    if (isNewAPI) {
      const templateType = "comparative comparison tab";
      return this.http.get(
        `${this.apiEP}/addin/export?${params}&template_type=${templateType}`,
      );
    }
    return this.http.get(`${this.apiEP}/comps_export/export?${params}`);
  }

  exportTrends(params, isNewAPI = false) {
    if (isNewAPI)
      return this.http.get(
        `${this.apiEP}/addin/export?${params}&template_type=trends`,
      );
    return this.http.get(`${this.apiEP}/trends_export/export?${params}`);
  }

  exportIndustry(params) {
    return this.http.get(`${this.apiEP}/addin/export?${params}`);
  }

  downloadComps(trackerId) {
    return this.http.get(
      `${this.apiEP}/api/excel_file_download?exceltrackerid=${trackerId}`,
    );
  }

  getUserCompId(params): Observable<any> {
    let urlParam = "";
    if (!(Object.keys(params).length === 0 && params.constructor === Object)) {
      urlParam = `?accepted_comps=${params["accepted_comps"]}&shared_id=${params["shared_id"]}`;
    }
    return this.http.get(`${this.apiEP}/comps/get_user_comp_id${urlParam}`);
  }

  saveNewComp(
    compName,
    tickerInfo,
    lineItemInfo,
    tabType,
    period,
    yearType,
    currency,
    dataView,
    isActive,
    marketItems?,
  ) {
    lineItemInfo = lineItemInfo.map((li) => {
      return {
        ...li,
        sli_comps_id: 0,
      };
    });
    tickerInfo = tickerInfo.map((ti) => {
      return {
        ...ti,
        sli_comps_id: 0,
      };
    });
    let reqObj = {};
    if (period !== null) {
      reqObj = {
        tab_name: compName,
        ticker_info: tickerInfo,
        line_item_info: lineItemInfo,
        tab_type: tabType,
        period,
        year_type: yearType,
        market_data: marketItems,
      };
    } else {
      reqObj = {
        tab_name: compName,
        ticker_info: tickerInfo,
        line_item_info: lineItemInfo,
        tab_type: tabType,
        market_data: marketItems,
      };
    }

    if (dataView && dataView.view_by) {
      reqObj["comp_meta_data"] = dataView;
    }

    if (currency) {
      if (currency === "Base") {
        reqObj["currency"] = null;
      } else {
        reqObj["currency"] = currency;
      }
    }
    if (isActive === false) {
      reqObj["is_active"] = false;
    }
    return this.http.post(`${this.apiEP}/comps/add_new_comps_table`, reqObj);
  }

  getPeriods() {
    return this.http.get(`${this.apiEP}/comps/get_period`);
  }

  getUserSuggestions(searchQuery) {
    return this.http.get(
      `${this.apiEP}/watcher/get_user_suggestions/?q=${searchQuery}&internal_user_filter=true`,
    );
  }

  shareComps(tabId: number, emailId: string, is_cc: boolean): Observable<any> {
    let params = `recipient_email=${encodeURIComponent(
      emailId,
    )}&tab_id=${tabId}`;
    if (is_cc) {
      params += `&is_cc=${is_cc}`;
    }
    return this.http.get(`${this.apiEP}/comps/share_comps_table?${params}`);
  }

  getSharedCompsHistory(tabId: number) {
    return this.http.get(
      `${this.apiEP}/comps/shared_comps_history?tab_id=${tabId}`,
    );
  }

  getCurrencyMessage(currency: string): Observable<any> {
    if (currency === "Base") {
      return this.http.get(`${this.apiEP}/comps/get_currency_message`);
    }
    return this.http.get(
      `${this.apiEP}/comps/get_currency_message?currency=${currency}`,
    );
  }

  getCompanyPeers(
    tickers: Array<string>,
    sliIds: Array<number>,
    limit = 10,
    peersBySli = false,
    ignoreMpt = false,
  ): Observable<any> {
    if (sliIds && sliIds.length) {
      return this.http
        .get(
          `${this.apiEP}/charts_page/get_company_peers?ticker=${tickers.join(
            "&ticker=",
          )}&uniquesliid=${sliIds.join(
            "&uniquesliid=",
          )}&limit=${limit}&peers_by_usli=${peersBySli}`,
        )
        .pipe(catchError((error) => of(error)));
    }
    return this.http
      .get(
        `${this.apiEP}/charts_page/get_company_peers?ticker=${tickers.join(
          "&ticker=",
        )}&limit=${limit}&ignore_mpt=${ignoreMpt}`,
      )
      .pipe(catchError((error) => of(error)));
  }

  getDataCallApiThreshold(): Observable<any> {
    return this.http.get(
      `${this.apiEP}/comps/get_threshhold_for_compsview_optimization`,
    );
  }
}
